import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import Sidebar from '../../component/Sidebar';
import Topbar from '../../component/Topbar';
import { getCompanyConfig } from '../utils/companyApi';

// Reuse ALL tenant dashboard components
import DashboardNew from '../../component/user/DashboardNew';
import FineOps from '../../component/user/FineOps';
import SecureGuard from '../../component/user/SecureGuard';
import SecureGuardCompliance from '../../component/user/SecureGuardCompliance';
import SecureGuardComplianceDetail from '../../component/user/SecureGuardComplianceDetail';
import WellArchitectedNew from '../../component/waf/WellArchitectedNew';
import CloudMapPage from '../../component/user/CloudMapPage';
import CloudMapDiagram from '../../components/CloudMap/CloudMapDiagram';
import DefenderScanTable from '../../component/user/DefenderScanTable';
import DefenderScanDetail from '../../component/user/DefenderScanDetail';
import CommandCenter from '../../component/user/CommandCenter';
import CompanyUsers from './CompanyUsers';

/**
 * 🏢 Company Dashboard
 * 
 * CRITICAL: This component REUSES all tenant components
 * Zero code duplication - components read companyID from localStorage
 * Same UI/UX as tenant portal - just filtered by company instead of tenant
 */
const CompanyDashboard = () => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [companyID, setCompanyID] = useState(null);
  const [companyName, setCompanyName] = useState('Company Portal');
  const [mspID, setMspID] = useState(null);
  const [companyConfig, setCompanyConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const location = useLocation();

  useEffect(() => {
    console.log('CompanyDashboard mounted', {
      pathname: location.pathname,
      companyID: localStorage.getItem('companyID'),
      userType: localStorage.getItem('userType')
    });
    return () => console.log('CompanyDashboard unmounted', location.pathname);
  }, []);

  useEffect(() => {
    // Load company context from localStorage
    const storedCompanyID = localStorage.getItem('companyID');
    const storedUserType = localStorage.getItem('userType');

    if (!storedCompanyID || storedUserType !== 'company') {
      console.error('Invalid company session - redirecting to login');
      window.location.href = '/login';
      return;
    }

    // Set company ID and load config in ONE effect to prevent loop
    setCompanyID(storedCompanyID);
    loadCompanyConfig(storedCompanyID);
  }, []); // Only run once on mount - FIXED: Don't watch companyID

  const loadCompanyConfig = async (companyID) => {
    try {
      setLoading(true);
      const config = await getCompanyConfig(companyID);
      
      if (config) {
        setCompanyConfig(config);
        setCompanyName(config.companyName || 'Company Portal');
        setMspID(config.mspID);
      }
    } catch (error) {
      console.error('Error loading company config:', error);
    } finally {
      setLoading(false);
    }
  };

  // Map of routes to page titles (company-specific)
  const routeTitles = [
    { path: "/company/dashboard", title: "Company Dashboard" },
    { path: "/company/users", title: "Company Users" },
    { path: "/company/cloudmap", title: "Cloud Map" },
    { path: "/company/cloudmap-diagram", title: "CloudMap - Architecture Diagram" },
    { path: "/company/secureguard", title: "Secure Guard" },
    { path: "/company/secureguard/compliance", title: "Secure Guard Compliance" },
    { path: "/company/secureguard/compliance/:slug", title: "Compliance Details" },
    { path: "/company/well-architected", title: "AWS Well-Architected Framework" },
    { path: "/company/defender-scan", title: "Defender Scan" },
    { path: "/company/defender-scan/details/:slug", title: "Defender Scan Details" },
    { path: "/company/fineops", title: "FinOps" },
    { path: "/company/command-center", title: "Command Center" },
  ];

  useEffect(() => {
    const currentPath = location.pathname;

    // Find matching route title or use default
    const matchedRoute = routeTitles.find((route) => {
      const regex = new RegExp(`^${route.path.replace(/:\w+/g, "\\w+")}$`);
      return regex.test(currentPath);
    });

    document.title = matchedRoute ? matchedRoute.title : companyName;
  }, [location.pathname]); // Only depend on pathname, not companyName

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  useEffect(() => {
    const handleResize = () => {
      setIsSidebarOpen(window.innerWidth > 768);
    };
    window.addEventListener('resize', handleResize);
    handleResize();
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  if (!companyID || loading) {
    return <div>Loading...</div>;
  }

  return (
    <div className="dashboard">
      {/* Sidebar - pass companyMode prop to indicate company context */}
      <Sidebar 
        isVisible={isSidebarOpen} 
        toggleSidebar={toggleSidebar}
        companyMode={true}
      />

      {/* Main Content */}
      <div
        className="main-content"
        style={{
          marginLeft: isSidebarOpen ? '250px' : '80px',
          transition: 'margin-left 0.3s ease',
        }}
      >
        <Topbar 
          isSidebarOpen={isSidebarOpen} 
          toggleSidebar={toggleSidebar}
          companyMode={true}
          companyName={companyName}
        />
        <div style={{ paddingTop: '0px' }}>
          <Routes>
            <Route 
              path="dashboard" 
              element={<DashboardNew isSidebarOpen={isSidebarOpen} />} 
            />
            <Route 
              path="users" 
              element={
                <CompanyUsers 
                  companyID={companyID}
                  mspID={mspID}
                  companyConfig={companyConfig}
                  isSidebarOpen={isSidebarOpen}
                />
              } 
            />
            <Route 
              path="cloudmap" 
              element={<CloudMapPage isSidebarOpen={isSidebarOpen} />} 
            />
            <Route 
              path="cloudmap-diagram" 
              element={<CloudMapDiagram />} 
            />
            <Route 
              path="secureguard" 
              element={<SecureGuard isSidebarOpen={isSidebarOpen} />} 
            />
            <Route 
              path="secureguard/compliance" 
              element={<SecureGuardCompliance isSidebarOpen={isSidebarOpen} />} 
            />
            <Route 
              path="secureguard/compliance/:slug" 
              element={<SecureGuardComplianceDetail isSidebarOpen={isSidebarOpen} />} 
            />
            <Route 
              path="well-architected" 
              element={<WellArchitectedNew />} 
            />
            <Route 
              path="defender-scan" 
              element={<DefenderScanTable isSidebarOpen={isSidebarOpen} />} 
            />
            <Route 
              path="defender-scan/details/:slug" 
              element={<DefenderScanDetail isSidebarOpen={isSidebarOpen} />} 
            />
            <Route 
              path="fineops" 
              element={<FineOps isSidebarOpen={isSidebarOpen} />} 
            />
            <Route 
              path="command-center" 
              element={<CommandCenter isSidebarOpen={isSidebarOpen} />} 
            />
            {/* Catch-all redirect */}
            <Route path="*" element={<Navigate to="dashboard" replace />} />
          </Routes>
        </div>
      </div>
    </div>
  );
};

export default CompanyDashboard;
